import { useState, useEffect } from 'react';
import API, { fmt, exportFile } from '../utils/api';

const EXPORTS = [
  { type:'customers',  label:'العملاء',        icon:'👥' },
  { type:'orders',     label:'الطلبات',         icon:'🛒' },
  { type:'payments',   label:'الدفعات',         icon:'💵' },
  { type:'products',   label:'المنتجات',        icon:'📦' },
  { type:'movements',  label:'حركات المخزون',   icon:'🔄' },
];

function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.max(4, (value / max) * 100) : 4;
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:36 }}>
      <span style={{ fontSize:'0.62rem', color:'var(--muted)', fontWeight:600 }}>
        {value > 0 ? fmt.currency(value).replace('$ ','') : '-'}
      </span>
      <div style={{
        width:'100%', height: `${pct}%`, maxHeight:130, minHeight:4,
        background: `linear-gradient(180deg, ${color} 0%, ${color}88 100%)`,
        borderRadius:'4px 4px 0 0', transition:'height 0.4s ease',
        cursor:'default'
      }} />
    </div>
  );
}

export default function Reports() {
  const [period,    setPeriod]    = useState('monthly');
  const [data,      setData]      = useState([]);
  const [exporting, setExporting] = useState('');
  const [loading,   setLoading]   = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get(`/reports/sales?period=${period}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [period]);

  const doExport = async (type) => {
    setExporting(type);
    try { await exportFile(type); }
    catch { alert('فشل التصدير'); }
    finally { setExporting(''); }
  };

  const maxSales = Math.max(...data.map(d => d.total_sales), 1);
  const totals = {
    sales:     data.reduce((s,d) => s + d.total_sales, 0),
    paid:      data.reduce((s,d) => s + d.total_paid, 0),
    remaining: data.reduce((s,d) => s + d.total_remaining, 0),
    orders:    data.reduce((s,d) => s + d.orders_count, 0),
  };
  const collectRate = totals.sales > 0 ? ((totals.paid / totals.sales) * 100).toFixed(1) : 0;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">التقارير</h1>
          <div className="page-title-sub">تحليل المبيعات والتحصيل</div>
        </div>
        <div className="page-actions">
          {EXPORTS.map(({ type, label, icon }) => (
            <button key={type} className="btn btn-secondary btn-sm"
              onClick={() => doExport(type)} disabled={!!exporting}>
              {exporting === type
                ? <><span className="spinner" style={{ width:12, height:12 }}/> جاري...</>
                : <>{icon} {label}</>}
            </button>
          ))}
        </div>
      </div>

      {/* Period Tabs */}
      <div className="tabs" style={{ marginBottom:20, maxWidth:300 }}>
        {[['daily','يومي'],['monthly','شهري'],['yearly','سنوي']].map(([v,l]) => (
          <button key={v} className={`tab ${period===v?'active':''}`} onClick={() => setPeriod(v)}>{l}</button>
        ))}
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom:20 }}>
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background:'var(--success)' }} />
          <div className="stat-icon" style={{ background:'var(--success-bg)' }}>💰</div>
          <div className="stat-value" style={{ color:'var(--success)' }}>{fmt.currency(totals.sales)}</div>
          <div className="stat-label">إجمالي المبيعات</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background:'var(--info)' }} />
          <div className="stat-icon" style={{ background:'var(--info-bg)' }}>✅</div>
          <div className="stat-value" style={{ color:'var(--info)' }}>{fmt.currency(totals.paid)}</div>
          <div className="stat-label">إجمالي المحصّل</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background: totals.remaining > 0 ? 'var(--danger)' : 'var(--success)' }} />
          <div className="stat-icon" style={{ background: totals.remaining > 0 ? 'var(--danger-bg)' : 'var(--success-bg)' }}>⏳</div>
          <div className="stat-value" style={{ color: totals.remaining > 0 ? 'var(--danger)' : 'var(--success)' }}>
            {fmt.currency(totals.remaining)}
          </div>
          <div className="stat-label">إجمالي المتبقي</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background:'var(--brand)' }} />
          <div className="stat-icon" style={{ background:'var(--brand-light)' }}>🛒</div>
          <div className="stat-value">{totals.orders}</div>
          <div className="stat-label">عدد الطلبات</div>
        </div>
      </div>

      {/* Collection Rate Banner */}
      {totals.sales > 0 && (
        <div style={{
          background:'var(--surface)', border:'1px solid var(--border)',
          borderRadius:12, padding:'16px 20px', marginBottom:20,
          display:'flex', alignItems:'center', gap:16, flexWrap:'wrap'
        }}>
          <div>
            <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.5px' }}>
              نسبة التحصيل الإجمالية
            </div>
            <div style={{ fontSize:'1.6rem', fontWeight:800, color: collectRate >= 80 ? 'var(--success)' : collectRate >= 50 ? 'var(--warning)' : 'var(--danger)' }}>
              {collectRate}%
            </div>
          </div>
          <div style={{ flex:1, minWidth:200 }}>
            <div className="progress" style={{ height:10 }}>
              <div className="progress-bar" style={{
                width: collectRate + '%',
                background: collectRate >= 80 ? 'var(--success)' : collectRate >= 50 ? 'var(--warning)' : 'var(--danger)'
              }} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:5, fontSize:'0.75rem', color:'var(--muted)' }}>
              <span>محصّل: {fmt.currency(totals.paid)}</span>
              <span>متبقي: {fmt.currency(totals.remaining)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-title">مخطط المبيعات</div>
        {loading ? (
          <div className="loading"><div className="spinner" /><span>جاري التحميل...</span></div>
        ) : data.length === 0 ? (
          <div className="empty-state" style={{ padding:'32px 0' }}>
            <div className="empty-state-icon">📊</div>
            <p>لا توجد بيانات للفترة المحددة</p>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:170, overflowX:'auto', paddingBottom:4, paddingTop:8 }}>
            {data.slice().reverse().map((d, i) => (
              <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:42, flex:1 }}>
                <span style={{ fontSize:'0.62rem', color:'var(--muted)', fontWeight:600, textAlign:'center' }}>
                  {d.total_sales > 0 ? '$' + Math.round(d.total_sales).toLocaleString() : '—'}
                </span>
                <div style={{
                  width:'100%',
                  height: `${Math.max(4, (d.total_sales / maxSales) * 130)}px`,
                  background: 'linear-gradient(180deg, var(--brand) 0%, rgba(181,69,27,0.5) 100%)',
                  borderRadius:'5px 5px 0 0', transition:'height 0.4s ease',
                  position:'relative', cursor:'default',
                  minHeight:4
                }}>
                  {/* Paid overlay */}
                  {d.total_paid > 0 && (
                    <div style={{
                      position:'absolute', bottom:0, left:0, right:0,
                      height: `${(d.total_paid / d.total_sales) * 100}%`,
                      background:'rgba(13,158,96,0.35)', borderRadius:'0 0 5px 5px'
                    }} />
                  )}
                </div>
                <span style={{ fontSize:'0.65rem', color:'var(--muted)', textAlign:'center', whiteSpace:'nowrap' }}>{d.period}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display:'flex', gap:16, marginTop:10, fontSize:'0.75rem', color:'var(--muted)' }}>
          <span style={{ display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ width:12, height:12, borderRadius:2, background:'var(--brand)', display:'inline-block' }}/>
            إجمالي المبيعات
          </span>
          <span style={{ display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ width:12, height:12, borderRadius:2, background:'rgba(13,158,96,0.5)', display:'inline-block' }}/>
            المحصّل
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>الفترة</th>
                <th style={{ textAlign:'center' }}>الطلبات</th>
                <th>إجمالي المبيعات</th>
                <th>المحصّل</th>
                <th>المتبقي</th>
                <th style={{ minWidth:160 }}>نسبة التحصيل</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => {
                const rate = d.total_sales > 0 ? (d.total_paid / d.total_sales * 100) : 0;
                return (
                  <tr key={i}>
                    <td className="font-bold">{d.period}</td>
                    <td style={{ textAlign:'center' }}>
                      <span className="badge badge-info">{d.orders_count}</span>
                    </td>
                    <td className="font-bold text-success">{fmt.currency(d.total_sales)}</td>
                    <td className="text-success">{fmt.currency(d.total_paid)}</td>
                    <td className={d.total_remaining > 0 ? 'text-danger font-bold' : 'text-success'}>
                      {fmt.currency(d.total_remaining)}
                    </td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div className="progress" style={{ flex:1, minWidth:80 }}>
                          <div className="progress-bar" style={{
                            width: rate + '%',
                            background: rate >= 80 ? 'var(--success)' : rate >= 50 ? 'var(--warning)' : 'var(--danger)'
                          }} />
                        </div>
                        <span style={{
                          fontSize:'0.78rem', fontWeight:700, whiteSpace:'nowrap',
                          color: rate >= 80 ? 'var(--success)' : rate >= 50 ? 'var(--warning)' : 'var(--danger)'
                        }}>
                          {rate.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && !loading && (
                <tr><td colSpan={6}>
                  <div className="empty-state">
                    <div className="empty-state-icon">📊</div>
                    <p>لا توجد بيانات للفترة المحددة</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
